<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import StatCard from '@/Components/UI/StatCard.vue'
import PeriodSelector from '@/Components/UI/PeriodSelector.vue'
import FleetDonut from '@/Components/UI/FleetDonut.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import Icon from '@/Components/UI/Icon.vue'
import RevenueChart from './RevenueChart.vue'
import { useBreakpoint } from '@/composables/useBreakpoint.js'

defineOptions({ layout: AppLayout })

const { t } = useI18n()
const page = usePage()

const props = defineProps({
  stats:           Object,
  fleet:           Object,
  monthly_revenue: Array,
  overdue_rentals: Array,
  recent_rentals:  Array,
  recent_losses:   Array,
  period:          String,
  period_label:    String,
  period_from:     String,
  period_to:       String,
})

const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')
const permissions = computed(() => page.props.auth?.user?.permissions ?? {})
const permitted = (section) => isAdmin.value || permissions.value[section] !== false

const hasOverdue = computed(() => (props.stats?.overdue_rentals ?? 0) > 0)

// Recent + Overdue lists: server caps at 10. Render 10 on desktop (≥1024px),
// 5 on smaller viewports so phones/tablets don't get a wall of rows.
const { isDesktop } = useBreakpoint()
const visibleLimit = computed(() => isDesktop.value ? 10 : 5)
const visibleRecent = computed(() => (props.recent_rentals ?? []).slice(0, visibleLimit.value))
const visibleOverdue = computed(() => (props.overdue_rentals ?? []).slice(0, visibleLimit.value))

const periodQS = computed(() => {
  const parts = [`period=${props.period}`]
  if (props.period === 'custom') {
    if (props.period_from) parts.push(`date_from=${props.period_from}`)
    if (props.period_to)   parts.push(`date_to=${props.period_to}`)
  }
  return parts.join('&')
})
const rentalsHref = (status) => `/rentals?status=${status}&from=dashboard&${periodQS.value}`
const lossesHref = computed(() => `/losses?from=dashboard&${periodQS.value}`)
const rentalsIndexHref = computed(() => `/rentals?from=dashboard&${periodQS.value}`)

const overdueLabel = (r) => {
  if (r.hours_late < 24) return `+${r.hours_late}h ${t('rentals.overdue')}`
  return `+${r.days_late}d ${t('rentals.overdue')}`
}

const now = ref(new Date())
let nowTimer = null
onMounted(() => { nowTimer = setInterval(() => { now.value = new Date() }, 1000) })
onBeforeUnmount(() => { if (nowTimer) clearInterval(nowTimer) })

const dateLabel = computed(() =>
  now.value.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })
)
const timeLabel = computed(() =>
  now.value.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
)

const statusTone = (s) => ({
  active: 'ok', pending: 'warn', returned: 'info', cancelled: 'muted', overdue: 'danger',
}[s] || 'muted')
</script>

<template>
  <div class="flex flex-col gap-5">
    <!-- Hero + period -->
    <div class="flex flex-wrap items-end gap-4">
      <div class="flex-1 min-w-[200px]">
        <div class="flex items-baseline gap-2">
          <span class="text-[0.8125rem]" style="color: var(--muted); font-family: var(--body);">{{ dateLabel }} {{ timeLabel }}</span>
        </div>
      </div>
      <PeriodSelector :current="period" :from="period_from" :to="period_to" />
    </div>

    <!-- Stat cards -->
    <div class="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.active')"
        :value="stats.active_rentals"
        :live="true"
        :href="rentalsHref('active')"
      />
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.overdue')"
        :value="stats.overdue_rentals"
        :live="true"
        :pulse="stats.overdue_rentals > 0"
        :href="rentalsHref('overdue')"
      />
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.pending')"
        :value="stats.pending_rentals"
        :live="true"
        :href="rentalsHref('pending')"
      />
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.depositsHeld')"
        :value="stats.deposits_held"
        :live="true"
        :href="rentalsHref('deposits')"
      />
      <StatCard
        v-if="permitted('suppliers')"
        :label="t('dashboard.stats.depositsOwed')"
        :value="stats.deposits_owed"
        :live="true"
        href="/vehicle_sources?from=dashboard"
      />
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.outstanding')"
        :value="stats.outstanding_amount"
        :live="true"
        :href="rentalsHref('outstanding')"
      />
      <StatCard
        v-if="permitted('losses')"
        :label="t('dashboard.stats.losses')"
        :value="stats.losses_in_period"
        :period="period_label"
        :href="lossesHref"
      />
      <StatCard
        v-if="permitted('rentals')"
        :label="t('dashboard.stats.completed')"
        :value="stats.completed_in_period"
        unit="rentals"
        :period="period_label"
        :href="rentalsHref('returned')"
      />
      <StatCard
        :label="t('dashboard.stats.revenue')"
        :value="stats.revenue_current"
        :delta="stats.revenue_growth_pct"
        :period="period_label"
        :href="rentalsHref('returned')"
      />
      <StatCard
        v-if="permitted('operations')"
        :label="t('dashboard.stats.inDelivery')"
        :value="stats.deliveries_in_flight"
        :live="true"
        href="/deliveries?from=dashboard"
      />
    </div>

    <!-- Chart + fleet donut -->
    <div class="grid gap-3 grid-cols-1 md:grid-cols-[2fr_1fr]">
      <section class="panel">
        <div class="mono text-[0.625rem] uppercase tracking-[0.08em]" style="color: var(--muted);">Revenue composition</div>
        <RevenueChart :data="monthly_revenue" :period-label="period_label" class="mt-4" />
      </section>

      <section v-if="permitted('fleet')" class="panel">
        <div class="mono text-[0.625rem] uppercase tracking-[0.08em] mb-4" style="color: var(--muted);">Fleet</div>
        <FleetDonut :fleet="fleet" />
      </section>
    </div>

    <!-- Recent + overdue -->
    <div v-if="permitted('rentals')" class="grid gap-3 grid-cols-1 lg:grid-cols-2">
      <!-- Recent -->
      <section class="panel-flush">
        <header class="panel-header">
          <span class="mono text-[0.625rem] uppercase tracking-[0.08em]" style="color: var(--muted);">Recent rentals</span>
          <div class="flex-1" />
          <Link :href="rentalsIndexHref" class="link-xs">View all →</Link>
        </header>
        <ul role="list" class="divide-list">
          <li
            v-for="r in visibleRecent"
            :key="r.id"
            class="row"
          >
            <span
              class="status-dot"
              :style="{
                background: `var(--${statusTone(r.status)})`,
                boxShadow: `0 0 0 3px var(--${statusTone(r.status)}Soft)`,
              }"
            />
            <div class="flex-1 min-w-0">
              <div class="text-[0.875rem] font-medium truncate" style="color: var(--ink);">{{ r.customer_name }}</div>
              <div class="text-[0.75rem] truncate" style="color: var(--muted);">{{ r.vehicle_name }} · {{ r.status }}</div>
            </div>
            <span class="mono text-[0.75rem]" style="color: var(--inkSoft);">{{ r.total }}</span>
            <Link :href="`/rentals/${r.id}`" class="chev-btn"><Icon name="chev-r" :size="16" /></Link>
          </li>
        </ul>
      </section>

      <!-- Overdue -->
      <section class="panel-flush">
        <header class="panel-header">
          <span class="badge" :class="hasOverdue ? 'danger' : 'ok'">
            <span class="dot" />
            {{ hasOverdue ? `${stats.overdue_rentals} overdue` : t('dashboard.allOnTrack') }}
          </span>
          <div class="flex-1" />
          <Link v-if="hasOverdue" :href="rentalsHref('overdue')" class="link-xs">View all →</Link>
        </header>

        <EmptyState v-if="!hasOverdue" type="success" :title="t('dashboard.allOnTrack')" />

        <ul v-else role="list" class="divide-list">
          <li
            v-for="r in visibleOverdue"
            :key="r.id"
            class="row"
          >
            <div class="avatar" :style="{ background: 'var(--dangerSoft)', color: 'var(--danger)' }">
              {{ (r.customer_name || '?').split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[0.875rem] font-medium truncate" style="color: var(--ink);">{{ r.customer_name }}</div>
              <div class="text-[0.75rem]" style="color: var(--danger);">{{ overdueLabel(r) }} · {{ r.vehicle_name }}</div>
            </div>
            <a
              v-if="r.customer_phone"
              :href="`tel:${r.customer_phone}`"
              class="call-btn"
              :aria-label="`Call ${r.customer_name}`"
              @click.stop
            ><Icon name="phone" :size="16" /></a>
            <Link :href="`/rentals/${r.id}`" class="chev-btn"><Icon name="chev-r" :size="16" /></Link>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<style scoped>
.panel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 20px;
}
.panel-flush {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.panel-header {
  display: flex; align-items: center;
  padding: 16px 20px;
  min-height: 56px;
  border-bottom: 1px solid var(--line);
  gap: 10px;
  box-sizing: border-box;
}

.divide-list > li + li { border-top: 1px solid var(--line); }

.row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 20px;
  transition: background 120ms;
}
.row:hover { background: var(--surfaceAlt); }

.avatar {
  width: 36px; height: 36px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 600; flex-shrink: 0;
}

.status-dot { width: 8px; height: 8px; border-radius: 999px; flex-shrink: 0; }

.badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  font-size: 0.75rem; font-weight: 500;
}
.badge .dot { width: 6px; height: 6px; border-radius: 999px; background: currentColor; }
.badge.ok { background: var(--okSoft); color: var(--ok); }
.badge.danger { background: var(--dangerSoft); color: var(--danger); }

.call-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 10px;
  background: var(--accentSoft); color: var(--accent);
  transition: background 120ms;
}
.call-btn:hover { background: color-mix(in oklab, var(--accent) 20%, var(--accentSoft)); }

.chev-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 8px;
  color: var(--muted);
}
.chev-btn:hover { color: var(--ink); background: var(--surfaceAlt); }

.link-xs {
  font-size: 0.75rem; color: var(--accent);
}
.link-xs:hover { text-decoration: underline; }
</style>
